import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Meeting {
  id: string;
  title: string;
  description: string;
  scheduledAt?: string;
  date?: string;
  duration?: number; // in minutes
  location: string;
  meetingType?: string;
  type?: string;
  status: string;
  attendees?: Array<{
    id: string;
    name: string;
    avatar?: string;
    rsvp?: string;
  }>;
  agenda?: Array<{
    id: string;
    title: string;
    description?: string;
    duration?: number;
  }>;
  organizer?: {
    id: string;
    name: string;
    avatar?: string;
  };
  meetingLink?: string;
  minutes?: string;
  chamaId?: string;
  chamaName?: string;
}

const ChamaMeetings: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const navigate = useNavigate();
  const colors = getThemeColors('dark');
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [chamaMembers, setChamaMembers] = useState<any[]>([]);
  const [attendanceData, setAttendanceData] = useState<{[meetingId: string]: any[]}>({});

  useEffect(() => {
    if (chamaId) {
      loadMeetings();
      loadChamaMembers();
    }
  }, [chamaId]);

  useEffect(() => {
    if (meetings.length > 0) {
      loadAttendanceData();
    }
  }, [meetings]);

  const loadChamaMembers = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.CHAMA_MEMBERS(chamaId!), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setChamaMembers(data.data);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load chama members:', error);
    }
  };

  const loadAttendanceData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const attendancePromises = meetings.map(async (meeting) => {
        try {
          const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meeting.id}/attendance`, {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              return { meetingId: meeting.id, attendance: data.data };
            }
          }
        } catch (error) {
          console.error(`❌ Failed to load attendance for meeting ${meeting.id}:`, error);
        }
        return { meetingId: meeting.id, attendance: [] };
      });

      const attendanceResults = await Promise.all(attendancePromises);
      const attendanceMap: {[meetingId: string]: any[]} = {};
      attendanceResults.forEach(result => {
        attendanceMap[result.meetingId] = result.attendance;
      });

      setAttendanceData(attendanceMap);
    } catch (error) {
      console.error('❌ Failed to load attendance data:', error);
    }
  };

  const loadMeetings = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaMeetings: No auth token found');
        setMeetings([]);
        return;
      }
      const response = await fetch(API_ENDPOINTS.CHAMA_MEETINGS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });


      if (response.ok) {
        const data = await response.json();

        if (data.success && data.data) {
          setMeetings(data.data);
        } else {
          setMeetings([]);
        }
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('❌ ChamaMeetings: Failed to load meetings:', response.status, errorText);
        setMeetings([]);
      }
    } catch (error) {
      console.error('❌ ChamaMeetings: Error loading meetings:', error);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return colors.primary;
      case 'ongoing': return colors.success;
      case 'completed': return colors.info;
      case 'cancelled': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return '📅';
      case 'ongoing': return '🔴';
      case 'completed': return '✅';
      case 'cancelled': return '❌';
      default: return '❓';
    }
  };

  const getMeetingTypeIcon = (type: string) => {
    switch (type) {
      case 'online': return '💻';
      case 'physical': return '🏢';
      default: return '📅';
    }
  };

  const getRSVPColor = (rsvp: string) => {
    switch (rsvp) {
      case 'yes': return colors.success;
      case 'no': return colors.error;
      case 'maybe': return colors.warning;
      case 'pending': return colors.textSecondary;
      default: return colors.textSecondary;
    }
  };

  const filteredMeetings = meetings.filter(meeting => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'upcoming') {
      const meetingDate = meeting.scheduledAt || meeting.date;
      return meeting.status === 'scheduled' && meetingDate && new Date(meetingDate) > new Date();
    }
    return meeting.status === activeFilter;
  });

  const renderMeetingCard = (meeting: Meeting) => {
    const meetingDate = meeting.scheduledAt || meeting.date;
    const isUpcoming = meetingDate ? new Date(meetingDate) > new Date() : false;
    const isOngoing = meeting.status === 'ongoing';

    // Calculate attendance based on actual attendance data, similar to MeetingSummary
    const meetingAttendance = attendanceData[meeting.id] || [];
    let totalMembers = chamaMembers.length || 0;
    let presentCount = 0;

    if (meetingAttendance.length > 0) {
      // Use actual attendance data if available
      presentCount = meetingAttendance.filter((att: any) => att && att.isPresent).length;
      totalMembers = Math.max(totalMembers, meetingAttendance.length);
    } else if (meeting.attendees && meeting.attendees.length > 0) {
      // Fallback to meeting attendees data
      presentCount = meeting.attendees.filter(a => a.rsvp === 'yes').length;
      totalMembers = meeting.attendees.length;
    } else if (chamaMembers.length > 0) {
      // If no attendance data but we have chama members, assume all members are expected
      totalMembers = chamaMembers.length;
      presentCount = 0; // No attendance data means no one marked as present
    }

    const attendeeCount = presentCount;
    const totalAttendees = totalMembers;

    return (
      <div key={meeting.id} style={{ ...styles.meetingCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div style={styles.meetingHeader}>
          <div style={styles.meetingType}>
            <span style={{ fontSize: '24px' }}>{getMeetingTypeIcon(meeting.meetingType || meeting.type || 'physical')}</span>
          </div>
          <div style={styles.meetingInfo}>
            <div style={{ ...styles.meetingTitle, color: colors.text }}>{meeting.title}</div>
            <div style={{ ...styles.meetingDate, color: colors.textSecondary }}>
              {meetingDate ? (
                <>
                  {new Date(meetingDate).toLocaleDateString()} at {new Date(meetingDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </>
              ) : (
                'Date not available'
              )}
            </div>
          </div>
          <div style={{ ...styles.meetingStatus, backgroundColor: getStatusColor(meeting.status) + '20', border: `1px solid ${getStatusColor(meeting.status)}` }}>
            <span style={{ fontSize: '16px', marginRight: '4px' }}>{getStatusIcon(meeting.status)}</span>
            <span style={{ ...styles.statusText, color: getStatusColor(meeting.status) }}>
              {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
            </span>
          </div>
        </div>

        <div style={styles.meetingDetails}>
          <div style={styles.meetingDescription}>
            <div style={{ ...styles.descriptionLabel, color: colors.textSecondary }}>Description</div>
            <div style={{ ...styles.descriptionValue, color: colors.text }}>{meeting.description}</div>
          </div>

          <div style={styles.meetingMeta}>
            <div style={styles.metaItem}>
              <div style={{ ...styles.metaLabel, color: colors.textSecondary }}>Duration</div>
              <div style={{ ...styles.metaValue, color: colors.text }}>{meeting.duration} minutes</div>
            </div>
            <div style={styles.metaItem}>
              <div style={{ ...styles.metaLabel, color: colors.textSecondary }}>Location</div>
              <div style={{ ...styles.metaValue, color: colors.text }}>{meeting.location}</div>
            </div>
            <div style={styles.metaItem}>
              <div style={{ ...styles.metaLabel, color: colors.textSecondary }}>Attendees</div>
              <div style={{ ...styles.metaValue, color: colors.text }}>
                {totalAttendees > 0 ? `${attendeeCount}/${totalAttendees}` : (chamaMembers.length > 0 ? `0/${chamaMembers.length}` : 'No attendees')}
              </div>
            </div>
          </div>

          {/* Attendance Button */}
          <div style={styles.attendanceButtonContainer}>
            <button
              onClick={() => {
                const meetingType = meeting.meetingType || meeting.type;
                if (meetingType === 'physical') {
                  navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/attendance/physical`);
                } else if (meetingType === 'virtual') {
                  navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/attendance/online`);
                } else {
                  // Default to physical for hybrid or unknown types
                  navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/attendance/physical`);
                }
              }}
              style={{
                ...styles.attendanceButton,
                backgroundColor: (meeting.meetingType || meeting.type) === 'physical' ? colors.primary : colors.secondary
              }}
            >
              {(meeting.meetingType || meeting.type) === 'physical' ? '📊 Take Attendance' : '🎬 Join Meeting'}
            </button>
          </div>

          {meeting.organizer && (
            <div style={styles.organizerInfo}>
              <div style={{ ...styles.organizerLabel, color: colors.textSecondary }}>Organized by</div>
              <div style={styles.organizerDetails}>
                {meeting.organizer.avatar && (
                  <img src={meeting.organizer.avatar} alt={meeting.organizer.name} style={styles.organizerAvatar} />
                )}
                <div style={{ ...styles.organizerName, color: colors.text }}>{meeting.organizer.name}</div>
              </div>
            </div>
          )}

          {meeting.agenda && meeting.agenda.length > 0 && (
            <div style={styles.agendaSection}>
              <div style={{ ...styles.agendaTitle, color: colors.textSecondary }}>Agenda</div>
              <div style={styles.agendaList}>
                {meeting.agenda.slice(0, 3).map((item, index) => (
                  <div key={item.id} style={styles.agendaItem}>
                    <div style={styles.agendaNumber}>{index + 1}</div>
                    <div style={styles.agendaContent}>
                      <div style={{ ...styles.agendaItemTitle, color: colors.text }}>{item.title}</div>
                      {item.description && (
                        <div style={{ ...styles.agendaItemDescription, color: colors.textSecondary }}>{item.description}</div>
                      )}
                    </div>
                  </div>
                ))}
                {meeting.agenda.length > 3 && (
                  <div style={{ ...styles.moreAgenda, color: colors.textSecondary }}>
                    +{meeting.agenda.length - 3} more items
                  </div>
                )}
              </div>
            </div>
          )}

          {meeting.attendees && meeting.attendees.length > 0 && (
            <div style={styles.attendeesSection}>
              <div style={{ ...styles.attendeesTitle, color: colors.textSecondary }}>Attendees</div>
              <div style={styles.attendeesList}>
                {meeting.attendees.slice(0, 5).map((attendee) => (
                  <div key={attendee.id} style={styles.attendeeItem}>
                    {attendee.avatar ? (
                      <img src={attendee.avatar} alt={attendee.name} style={styles.attendeeAvatar} />
                    ) : (
                      <div style={{ ...styles.attendeeInitials, backgroundColor: getRSVPColor(attendee.rsvp || 'pending') + '20' }}>
                        {attendee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                    )}
                    <div style={{ ...styles.attendeeRSVP, color: getRSVPColor(attendee.rsvp || 'pending') }}>
                      {(attendee.rsvp || 'pending') === 'yes' ? '✓' : (attendee.rsvp || 'pending') === 'no' ? '✗' : (attendee.rsvp || 'pending') === 'maybe' ? '?' : '⏳'}
                    </div>
                  </div>
                ))}
                {meeting.attendees.length > 5 && (
                  <div style={{ ...styles.moreAttendees, color: colors.textSecondary }}>
                    +{meeting.attendees.length - 5}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={styles.meetingActions}>
          <button
            onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/summary`)}
            style={{ ...styles.meetingActionButton, backgroundColor: colors.primary }}
          >
            View Details
          </button>
          {(meeting.status === 'ongoing' || meeting.status === 'scheduled') && (
            <>
              {(meeting.meetingType === 'physical' || meeting.type === 'physical') && (
                <button
                  onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/attendance/physical`)}
                  style={{ ...styles.meetingActionButton, backgroundColor: colors.info }}
                >
                  Mark Physical Attendance
                </button>
              )}
              {(meeting.meetingType === 'virtual' || meeting.type === 'virtual') && (
                <button
                  onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/attendance/online`)}
                  style={{ ...styles.meetingActionButton, backgroundColor: colors.secondary }}
                >
                  Mark Online Attendance
                </button>
              )}
              {(meeting.meetingType === 'hybrid' || meeting.type === 'hybrid') && (
                <>
                  <button
                    onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/attendance/physical`)}
                    style={{ ...styles.meetingActionButton, backgroundColor: colors.info }}
                  >
                    Mark Physical Attendance
                  </button>
                  <button
                    onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meeting.id}/attendance/online`)}
                    style={{ ...styles.meetingActionButton, backgroundColor: colors.secondary }}
                  >
                    Mark Online Attendance
                  </button>
                </>
              )}
            </>
          )}
          {isUpcoming && (
            <button style={{ ...styles.meetingActionButton, backgroundColor: colors.success }}>
              {(meeting.meetingType || meeting.type) === 'online' ? 'Join Meeting' : 'Get Directions'}
            </button>
          )}
          {isOngoing && (
            <button style={{ ...styles.meetingActionButton, backgroundColor: colors.success }}>
              Join Now
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading meetings...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Chama Meetings"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>📅</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {meetings.filter(m => m.status === 'scheduled' && (m.scheduledAt || m.date) && new Date(m.scheduledAt || m.date!) > new Date()).length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Upcoming</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>🔴</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {meetings.filter(m => m.status === 'ongoing').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Ongoing</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>✅</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {meetings.filter(m => m.status === 'completed').length}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Completed</div>
              </div>
            </div>

            <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.summaryIcon}>👥</div>
              <div style={styles.summaryInfo}>
                <div style={{ ...styles.summaryValue, color: colors.text }}>
                  {Object.values(attendanceData).reduce((sum, attendance) => sum + attendance.filter((att: any) => att && att.isPresent).length, 0)}
                </div>
                <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Attendees</div>
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div style={styles.filterContainer}>
            {[
              { id: 'all', label: 'All Meetings', count: meetings.length },
              { id: 'upcoming', label: 'Upcoming', count: meetings.filter(m => m.status === 'scheduled' && (m.scheduledAt || m.date) && new Date(m.scheduledAt || m.date!) > new Date()).length },
              { id: 'ongoing', label: 'Ongoing', count: meetings.filter(m => m.status === 'ongoing').length },
              { id: 'completed', label: 'Completed', count: meetings.filter(m => m.status === 'completed').length },
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                style={{
                  ...styles.filterButton,
                  backgroundColor: activeFilter === filter.id ? colors.primary : 'transparent',
                  color: activeFilter === filter.id ? 'white' : colors.text,
                }}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* Meetings List */}
          <div style={styles.meetingsList}>
            {filteredMeetings.length > 0 ? (
              filteredMeetings.map(renderMeetingCard)
            ) : (
              <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                <div style={styles.emptyIcon}>📅</div>
                <div style={{ ...styles.emptyTitle, color: colors.text }}>No meetings found</div>
                <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
                  {activeFilter === 'all'
                    ? 'No meetings have been scheduled yet.'
                    : `No ${activeFilter} meetings found.`
                  }
                </div>
              </div>
            )}
          </div>

          {/* Create Meeting Button */}
          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button
              onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/create`)}
              style={{ ...styles.actionButton, backgroundColor: colors.primary }}
            >
              Schedule New Meeting
            </button>
            <button
              onClick={() => navigate(`/dashboard/chama/${chamaId}`)}
              style={{ ...styles.secondaryButton, border: `1px solid ${colors.border}`, color: colors.text }}
            >
              Back to Chama
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  summaryIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  summaryInfo: {
    flex: 1,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  filterContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  filterButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  meetingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  meetingCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  meetingHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  meetingType: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  meetingInfo: {
    flex: 1,
  },
  meetingTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  meetingDate: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  meetingStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  meetingDetails: {
    marginBottom: spacing.md,
  },
  meetingDescription: {
    marginBottom: spacing.md,
  },
  descriptionLabel: {
    fontSize: typography.fontSize.sm,
  },
  descriptionValue: {
    fontSize: typography.fontSize.base,
    marginTop: spacing.xs / 2,
  },
  meetingMeta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  metaItem: {
    // Container for meta item
  },
  metaLabel: {
    fontSize: typography.fontSize.sm,
  },
  metaValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  organizerInfo: {
    marginBottom: spacing.md,
  },
  organizerLabel: {
    fontSize: typography.fontSize.sm,
  },
  organizerDetails: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing.xs / 2,
  },
  organizerAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: spacing.sm,
  },
  organizerName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  agendaSection: {
    marginBottom: spacing.md,
  },
  agendaTitle: {
    fontSize: typography.fontSize.sm,
  },
  agendaList: {
    marginTop: spacing.xs,
  },
  agendaItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  agendaNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(33, 150, 243, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    color: '#2196F3',
    marginRight: spacing.sm,
    flexShrink: 0,
  },
  agendaContent: {
    flex: 1,
  },
  agendaItemTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  agendaItemDescription: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  moreAgenda: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    padding: spacing.xs,
  },
  attendeesSection: {
    marginBottom: spacing.md,
  },
  attendeesTitle: {
    fontSize: typography.fontSize.sm,
  },
  attendeesList: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing.xs,
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  attendeeItem: {
    position: 'relative',
  },
  attendeeAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  attendeeInitials: {
    width: 32,
    height: 32,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    color: 'white',
  },
  attendeeRSVP: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    border: '2px solid white',
  },
  moreAttendees: {
    fontSize: typography.fontSize.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: borderRadius.md,
  },
  meetingActions: {
    display: 'flex',
    gap: spacing.sm,
  },
  meetingActionButton: {
    flex: 1,
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  actionButtons: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  actionButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  secondaryButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    backgroundColor: 'transparent',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
};

export default ChamaMeetings;